import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/base/ArticleCard';
import CTASection from '@/pages/home/components/CTASection';
import StructuredData from '@/components/base/StructuredData';
import { getArticleBySlug } from '@/mocks/articleData';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="pt-24 pb-16 text-center min-h-screen flex items-center justify-center">
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground-950">文章未找到</h1>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <StructuredData
        type="article"
        categoryName={article.category}
        articleTitle={article.title}
        articleDate={article.date}
        articleImage={article.image}
        articleAuthor={article.author}
      />
      <Navbar />
      <main>
        <article className="pt-24 pb-8 md:pt-32 md:pb-12 bg-background-50">
          <div className="px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <Link to="/category/all" className="hover:text-primary-500 transition-colors cursor-pointer">{article.category}</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-800 font-medium truncate max-w-[200px]">{article.title}</span>
              </nav>

              <div className="flex justify-center mb-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 text-foreground-600 text-sm">
                  <i className="ri-time-line"></i>
                  <span className="whitespace-nowrap">{article.date}</span>
                  <span className="text-foreground-300">·</span>
                  <span className="whitespace-nowrap">{article.author}</span>
                </div>
              </div>

              <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-black text-foreground-950 text-center leading-tight">
                {article.title}
              </h1>

              <p className="mt-5 text-base md:text-lg text-foreground-600 text-center max-w-3xl mx-auto leading-relaxed">
                {article.excerpt}
              </p>

              <div className="flex items-center justify-center gap-3 mt-6">
                <button
                  type="button"
                  className="px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-thumb-up-line mr-1.5"></i>点赞
                </button>
                <button
                  type="button"
                  className="px-5 py-2.5 rounded-full bg-background-50 text-foreground-800 text-sm font-medium border border-foreground-200 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-bookmark-line mr-1.5"></i>收藏
                </button>
              </div>

              <div className="mt-10 rounded-3xl overflow-hidden">
                <img
                  src={article.image}
                  alt={article.title}
                  title={article.title}
                  className="w-full h-[300px] md:h-[500px] object-cover object-top"
                />
              </div>
            </div>
          </div>
        </article>

        <section className="py-12 md:py-16 bg-background-50">
          <div className="px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <div className="prose-custom">
                {article.content.map((block, index) => {
                  if (block.type === 'heading') {
                    return (
                      <h2 key={index} className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4">
                        {block.text}
                      </h2>
                    );
                  }
                  return (
                    <p key={index} className="text-base md:text-lg text-foreground-700 leading-relaxed mb-5">
                      {block.text}
                    </p>
                  );
                })}
              </div>

              <div className="flex flex-wrap gap-2 mt-10 pt-8 border-t border-background-200">
                {article.tags.map((tag) => (
                  <Link
                    key={tag}
                    to="/category/all"
                    className="px-4 py-1.5 rounded-full bg-background-100 text-foreground-600 text-sm hover:bg-primary-100 hover:text-primary-700 transition-colors cursor-pointer"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-100">
          <div className="px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-8">
                <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                  爆料人评价
                </h3>
              </div>

              <div className="bg-background-50 rounded-2xl p-6 md:p-8">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 md:w-16 md:h-16 rounded-full overflow-hidden bg-background-200">
                      <img
                        src={article.reviewer.avatar}
                        alt={article.reviewer.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-medium">
                        <i className="ri-star-fill text-accent-500"></i>
                        {article.reviewer.rating}
                      </span>
                      <span className="text-sm font-medium text-foreground-900">{article.reviewer.name}</span>
                    </div>
                    <p className="text-sm text-foreground-500 mb-3">{article.reviewer.contact}</p>
                    <blockquote className="text-base md:text-lg text-foreground-700 italic leading-relaxed border-l-3 border-primary-300 pl-4">
                      "{article.reviewer.comment}"
                    </blockquote>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-background-50">
          <div className="px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-10 gap-4">
                <div>
                  <p className="text-sm font-medium text-foreground-400 uppercase tracking-wider mb-1">
                    相关推荐
                  </p>
                  <h2 className="font-heading text-2xl md:text-4xl font-black text-foreground-950 italic">
                    相关黑料
                  </h2>
                </div>
                <div className="max-w-md">
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    更多同类型爆料，让你一次吃个够。
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {article.relatedArticles.map((related, index) => (
                  <ArticleCard
                    key={related.id}
                    title={related.title}
                    category={related.category}
                    categorySlug={related.categorySlug}
                    date={related.date}
                    image={related.image}
                    slug={related.slug}
                    variant={index === 1 ? 'accent' : 'default'}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        <CTASection />
      </main>
      <Footer />
    </>
  );
}